import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'router-v6'
import { CashfreeRechargeModal } from 'v2/pages/billing/overview/cashfree-recharge-modal'
import { RazorpayCustomRechargeModal } from 'v2/pages/billing/overview/razorpay-custom-recharge-modal'
import { RechargeModal } from 'v2/pages/billing/overview/recharge-modal'
import {
  useAccountDetailsQuery,
  useBillingBalanceQuery,
  useBillingCardsQuery,
  useConversionRate,
  useUsageSummaryQuery,
} from 'v2/pages/billing/queries'
import { useIsCashfreeProvider, useIsRazorpayProvider } from 'v2/pages/billing/use-billing-provider'
import { useIndiaComplianceFirstRun } from 'v2/pages/settings/phone-numbers/use-india-compliance-first-run'
import {
  DEFAULT_USAGE_LIMIT_MAX,
  useGetAuthIdAndTokenQuery,
  useProfileQuery,
  useUsageLimitQuery,
} from 'v2/pages/settings/queries'
import { useSandboxSetupVisible } from 'v2/pages/settings/sandbox-numbers/use-sandbox-setup-visible'
import { RestrictedTokenNotice } from 'v2/shared/components/restricted-token-notice'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { useTokenAccess } from 'v2/shared/hooks/use-token-access'
import { formatCurrency } from 'v2/shared/utils'
import dayjs from 'v2/utils/dayjs'
import { CLIAuthorizedModal } from './cli-authorized-modal'
import { DecryptedText } from './decrypted-text'
import { KycRequiredModal, shouldRequireKycBeforeCredits } from './kyc-required-modal'
import { shouldBlankMonthlyUsage } from './monthly-usage'
import { SandboxSetupSection } from './sandbox-setup-section'
import { SetupSection } from './setup-cards'

const BILLING_PATH = '/billing'

// Rows of the /account panel: the panel frame owns the outer border and the
// grid's gap-px paints the hairlines between rows, so a row draws neither.
const ROW_CLASS =
  'min-w-0 bg-background px-3 sm:px-5 min-h-12 py-1.5 flex flex-wrap sm:flex-nowrap items-center gap-3'
const ROW_LABEL_CLASS = 'font-jetbrains text-label uppercase text-secondary shrink-0'

// Exported for tests. When rendering with `masked`, key the element by its value
// (as LandingDashboard does) so a credential refresh/org switch remounts the card:
// the reveal decision resets and the previous secret can never carry over.
export const CredentialCard = ({
  label,
  value,
  masked = false,
  restricted = false,
  testId,
}: {
  label: string
  value: string | undefined
  masked?: boolean
  restricted?: boolean
  testId: string
}) => {
  const [revealed, setRevealed] = useState(false)
  const copyButton = (
    <ClipboardCopyButton
      textToCopy={value ?? ''}
      tooltipText={`Copy ${label}`}
      className="h-7 w-7"
      iconSize={14}
      disabled={!value}
      data-testid={`${testId}-copy-btn`}
    />
  )

  if (restricted) {
    return (
      <div className={ROW_CLASS} data-testid={`${testId}-card`}>
        <span className={ROW_LABEL_CLASS}>{label}</span>
        <RestrictedTokenNotice testId={`${testId}-restricted`} />
      </div>
    )
  }

  return (
    <div className={ROW_CLASS} data-testid={`${testId}-card`}>
      <span className={ROW_LABEL_CLASS}>{label}</span>
      {masked ? (
        <>
          <span
            className="flex-1 min-w-0 truncate text-p-400 text-primary font-jetbrains"
            data-testid={`${testId}-value`}
          >
            {revealed && value ? <DecryptedText text={value} /> : '•••••••••••••••••'}
          </span>
          <div className="flex items-center shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hover:bg-transparent"
              disabled={!value}
              onClick={() => setRevealed((prev) => !prev)}
              data-testid={`${testId}-toggle`}
            >
              {revealed ? (
                <Eye className="h-4 w-4" aria-hidden="true" />
              ) : (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              )}
              <span className="sr-only">{revealed ? `Hide ${label}` : `Show ${label}`}</span>
            </Button>
            {copyButton}
          </div>
        </>
      ) : (
        <>
          <Input
            value={value ?? '—'}
            disabled
            readOnly
            variant="ghost"
            inputClassName="truncate bg-transparent text-p-400 text-primary font-jetbrains"
            className="min-w-0 flex-1"
            data-testid={`${testId}-value`}
          />
          <div className="shrink-0">{copyButton}</div>
        </>
      )}
    </div>
  )
}

const BillingCard = ({
  label,
  value,
  isLoading,
  ctaLabel,
  ctaTo,
  onCtaClick,
  testId,
}: {
  label: string
  value: string | undefined
  isLoading?: boolean
  ctaLabel?: string
  ctaTo?: string
  onCtaClick?: () => void
  testId: string
}) => (
  <div className={ROW_CLASS} data-testid={`${testId}-card`}>
    <span className={ROW_LABEL_CLASS}>{label}</span>
    {isLoading ? (
      <span className="flex-1 text-p-400 text-tertiary animate-pulse">...</span>
    ) : (
      <Input
        value={value ?? '—'}
        disabled
        readOnly
        variant="ghost"
        inputClassName="bg-transparent text-p-400 text-primary font-jetbrains"
        className="min-w-0 flex-1"
        data-testid={`${testId}-value`}
      />
    )}
    {ctaLabel &&
      (onCtaClick ? (
        <button
          type="button"
          onClick={onCtaClick}
          className="focus-visible rounded group/cta font-jetbrains text-label uppercase text-brand-text shrink-0 whitespace-nowrap"
          data-testid={`${testId}-cta`}
        >
          {ctaLabel}{' '}
          <span className="inline-block transition-transform group-hover/cta:translate-x-0.5">
            →
          </span>
        </button>
      ) : (
        <Link
          to={ctaTo ?? '#'}
          className="focus-visible rounded group/cta font-jetbrains text-label uppercase text-brand-text shrink-0 whitespace-nowrap"
          data-testid={`${testId}-cta`}
        >
          {ctaLabel}{' '}
          <span className="inline-block transition-transform group-hover/cta:translate-x-0.5">
            →
          </span>
        </Link>
      ))}
  </div>
)

export const LandingDashboard = () => {
  const { data: profile } = useProfileQuery()
  const { data: accountDetails } = useAccountDetailsQuery()
  const isProfileLoaded = profile !== undefined
  // Always fetch: the backend returns the auth id to every role and withholds only the
  // token, so the Auth ID card stays populated while the token card is gated below.
  const { canViewToken } = useTokenAccess()
  const { data: authData } = useGetAuthIdAndTokenQuery(isProfileLoaded ? 'auth_details' : undefined)
  const { data: balance, isLoading: isBalanceLoading } = useBillingBalanceQuery()
  const { data: currentMonthUsage, isLoading: isUsageSummaryLoading } = useUsageSummaryQuery()
  const { conversionRate: rawConversionRate, currencyCode } = useConversionRate()
  const conversionRate = rawConversionRate > 0 ? rawConversionRate : 1

  const accountIdForUsage = profile?.organizations?.[0]?.org_plivo_account_id
  const { data: usageLimitData, isLoading: isUsageLimitLoading } =
    useUsageLimitQuery(accountIdForUsage)
  // India orgs that haven't started KYC are on free-trial credits and have no
  // billed usage yet, so the card shows no number for them at all.
  const indiaFirstRun = useIndiaComplianceFirstRun()
  const blankMonthlyUsage = shouldBlankMonthlyUsage({
    isFirstRunLoading: indiaFirstRun.isLoading,
    isFirstRun: indiaFirstRun.isFirstRun,
  })
  // Gate on every async input the value depends on so the card doesn't briefly
  // show a confident wrong number using the default limit / 1x conversion —
  // including the first-run counts, which decide whether a number is shown at all.
  const isMonthlyUsageLoading =
    !blankMonthlyUsage &&
    (indiaFirstRun.isLoading ||
      isUsageSummaryLoading ||
      isUsageLimitLoading ||
      !(rawConversionRate > 0))
  const monthlyUsageLimit = Math.round(
    (usageLimitData?.limit_value ?? DEFAULT_USAGE_LIMIT_MAX) * conversionRate,
  )
  // currentMonthUsage is real spend from the Plivo UsageSummary API (already in
  // display currency). Undefined only if that call failed — then show just the
  // limit rather than a wrong/zero number.
  const monthlyUsageValue =
    blankMonthlyUsage || isMonthlyUsageLoading
      ? undefined
      : currentMonthUsage != null
        ? `${formatCurrency(currentMonthUsage, currencyCode, false)} of ${formatCurrency(monthlyUsageLimit, currencyCode, false)}`
        : formatCurrency(monthlyUsageLimit, currencyCode, false)

  const account = accountDetails?.account
  const org = profile?.organizations?.[0]
  const firstName = profile?.account_first_name || ''

  const balanceValue =
    balance?.amount != null
      ? formatCurrency(balance.amount, currencyCode, true, { minimumFractionDigits: 0 })
      : undefined

  const isPostpaid = account?.postpaid
  const paymentPeriodValue =
    isPostpaid && org?.payment_period != null ? `Net ${org.payment_period} Days` : undefined

  const navigate = useNavigate()
  const { data: cards } = useBillingCardsQuery()
  const isCashfreeProvider = useIsCashfreeProvider()
  const isRazorpayProvider = useIsRazorpayProvider()
  const { isTrial, trialEndDate, isTrialExpired } = useUserPlan()
  // Every sandbox branch on this page must read this one value: the panel owns
  // the page when it renders, so credits and the setup cards must not also.
  const inSandboxSetup = useSandboxSetupVisible()
  const [isRechargeOpen, setIsRechargeOpen] = useState(false)
  const [isKycRequiredOpen, setIsKycRequiredOpen] = useState(false)
  // India orgs must clear KYC before their credits are usable, so an org that
  // hasn't started one is sent to the KYC flow instead of a payment screen.
  // Scoped to prepaid: "Pay Now" settles a postpaid invoice, which must never
  // be blocked. Requires !isLoading — isFirstRun reads true while the counts
  // are still in flight, and failing open beats blocking a real payment.
  const needsKycBeforeCredits = shouldRequireKycBeforeCredits({
    isPostpaid,
    isLoading: indiaFirstRun.isLoading,
    isFirstRun: indiaFirstRun.isFirstRun,
  })
  // Add credits opens the recharge modal right here when the account can recharge
  // in place (prepaid, non-trial, card on file). Otherwise fall back to the
  // billing overview (add a card / trial upgrade / postpaid invoice live there).
  const canRechargeInPlace = !isPostpaid && !isTrial && (cards?.length ?? 0) > 0
  const handleAddCredits = () => {
    if (needsKycBeforeCredits) setIsKycRequiredOpen(true)
    else if (canRechargeInPlace) setIsRechargeOpen(true)
    else navigate(`${BILLING_PATH}/overview`)
  }

  return (
    <ScrollArea className="h-full">
      {/* Shown only when the URL carries ?cli_authorized=1 — the Plivo CLI's
          loopback handler redirects here after capturing the OAuth code. */}
      <CLIAuthorizedModal />
      {/* India, pre-KYC: Add Credits explains the prerequisite and links onward. */}
      <KycRequiredModal open={isKycRequiredOpen} onOpenChange={setIsKycRequiredOpen} />
      {/* Recharge in place from the credits tile (prepaid, non-trial). Mounts
          only for that case; opening is gated by handleAddCredits. The gateway
          fork mirrors billing/overview so this tile can't open a different PSP's
          modal than the Billing page does for the same account. */}
      {!isPostpaid && !isTrial && isRazorpayProvider && (
        <RazorpayCustomRechargeModal
          isOpen={isRechargeOpen}
          onClose={() => setIsRechargeOpen(false)}
        />
      )}
      {!isPostpaid && !isTrial && isCashfreeProvider && (
        <CashfreeRechargeModal isOpen={isRechargeOpen} onClose={() => setIsRechargeOpen(false)} />
      )}
      {!isPostpaid && !isTrial && !isCashfreeProvider && !isRazorpayProvider && (
        <RechargeModal
          isOpen={isRechargeOpen}
          onClose={() => setIsRechargeOpen(false)}
          isTrial={false}
          cards={cards}
        />
      )}
      <div className="px-6 py-6">
        <h1 className="text-h2-500 mb-5">
          {firstName ? (
            <>
              Welcome <span className="text-primary">{firstName}!</span>
            </>
          ) : (
            'Welcome back!'
          )}
        </h1>

        {/* /account panel — same chrome as the /get-started panel below, so the
            page reads as two framed console blocks rather than loose cards. */}
        <section className="overflow-hidden rounded-lg border border-st2">
          <div className="flex h-10 items-center justify-between border-b border-st2 bg-background dark:bg-bg2 px-4">
            <h2 className="font-jetbrains text-s-400 text-secondary">
              <span className="text-tertiary">/</span>account
            </h2>
            {account && (
              <span className="flex items-center gap-1.5 font-jetbrains text-label uppercase text-secondary">
                <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
                {isPostpaid ? 'Postpaid' : 'Prepaid'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-px bg-st2 lg:grid-cols-2">
            {/* Auth ID */}
            <CredentialCard
              label="Auth ID"
              value={authData?.data?.authId}
              testId="landing-auth-id"
            />

            {/* Remaining Credits / Payment Period */}
            {!inSandboxSetup && (
              <BillingCard
                label={isPostpaid ? 'Payment Period' : 'Remaining Credits'}
                value={isPostpaid ? paymentPeriodValue : balanceValue}
                isLoading={!isPostpaid && isBalanceLoading}
                ctaLabel={isPostpaid ? 'Pay Now' : 'Add Credits'}
                onCtaClick={handleAddCredits}
                testId="landing-balance"
              />
            )}

            {/* Auth Token */}
            <CredentialCard
              key={authData?.data?.newAuthToken || authData?.data?.authToken}
              label="Auth Token"
              value={authData?.data?.newAuthToken || authData?.data?.authToken}
              masked
              restricted={!canViewToken}
              testId="landing-auth-token"
            />

            {/* Trial accounts don't have a monthly usage limit — surface trial-credit
                expiry instead (SER-5859 / Ayush). CTA recharges/upgrades in both
                states; copy gets more urgent once expired. */}
            {inSandboxSetup ? null : isTrial ? (
              <BillingCard
                label="Trial Credits"
                value={
                  trialEndDate
                    ? `${isTrialExpired ? 'Expired on' : 'Expires on'} ${dayjs(trialEndDate).format('DD/MM/YYYY')}`
                    : undefined
                }
                ctaLabel={isTrialExpired ? 'Recharge now' : 'Recharge'}
                onCtaClick={handleAddCredits}
                testId="landing-trial-expiry"
              />
            ) : (
              <BillingCard
                label="Monthly Usage"
                value={monthlyUsageValue}
                isLoading={isMonthlyUsageLoading}
                ctaLabel="Manage limit"
                ctaTo="/account-settings"
                testId="landing-monthly-usage"
              />
            )}
          </div>
        </section>

        <div className="flex flex-col gap-6 mt-8">
          {inSandboxSetup ? <SandboxSetupSection /> : <SetupSection />}
        </div>
      </div>
    </ScrollArea>
  )
}
